"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShoppingCart } from "@fortawesome/free-solid-svg-icons";
import { fetchData, postData } from "@/utils/Api";

const getUnitPrice = (item) => {
  if (typeof item.unit_price === "number") return item.unit_price;
  if (typeof item.final_price === "number") return item.final_price;
  const n = Number(item.price || 0);
  return Number.isFinite(n) ? n : 0;
};

const transformCartItemsFromApi = (rawItems) =>
  (rawItems || []).map((it) => {
    const rawSelected = it.selectedOptions || it.SelectedOptions || {};
    const options = Object.entries(rawSelected).map(([field_name, label]) => ({
      field_name,
      label: String(label),
      price: 0,
    }));

    const qty =
      typeof it.quantity === "number" && it.quantity > 0
        ? it.quantity
        : typeof it.qty === "number" && it.qty > 0
          ? it.qty
          : 1;

    return { ...it, options, qty };
  });

const recalc = (items) =>
  items.reduce(
    (acc, item) => {
      const unit = getUnitPrice(item);
      const qty = Number(item.qty ?? item.quantity ?? 1);
      acc.count += qty;
      acc.total += unit * qty;
      return acc;
    },
    { count: 0, total: 0 }
  );

export default function CartMenu() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);

  const wrapRef = useRef(null);

  const summary = useMemo(() => recalc(items), [items]);

  const refresh = () => {
    fetchData("/api/store/cart/get", (data) => {
      const normalized = transformCartItemsFromApi(data?.cartItems ?? []);
      setItems(normalized);
    });
  };

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    const onRefresh = () => refresh();
    window.addEventListener("cart:refresh", onRefresh);
    return () => window.removeEventListener("cart:refresh", onRefresh);
  }, []);

  // click-outside closes
  useEffect(() => {
    const onDown = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const changeQty = async (item, delta) => {
    const itemId = item.id;
    const idx = items.findIndex((i) => i.id === itemId);
    if (idx === -1) return;

    const prev = [...items];
    const current = prev[idx];
    const currentQty = Number(current.qty) || 1;
    const stock = current.stock ?? 9999;
    const nextQty = Math.max(1, Math.min(currentQty + delta, stock));
    if (nextQty === currentQty) return;

    const optimistic = [...prev];
    optimistic[idx] = { ...optimistic[idx], qty: nextQty };
    setItems(optimistic);
    setUpdatingId(itemId);

    try {
      await postData("/api/store/cart/update", { cart_id: itemId, quantity: nextQty });
    } catch (e) {
      console.error("qty update failed", e);
      setItems(prev);
    } finally {
      setUpdatingId(null);
    }
  };

  const removeItem = async (itemId) => {
    const prev = [...items];
    setItems(prev.filter((i) => i.id !== itemId));
    setUpdatingId(itemId);

    try {
      await postData("/api/store/cart/delete", { cart_id: itemId });
    } catch (e) {
      console.error("delete failed", e);
      setItems(prev);
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div ref={wrapRef} className="position-relative ms-3">
      <button
        type="button"
        className="btn btn-outline-dark d-flex align-items-center gap-2"
        onClick={() => setOpen((p) => !p)}
      >
        <FontAwesomeIcon icon={faShoppingCart} />
        <span className="d-none d-lg-inline">
          {summary.count > 0 ? `$${summary.total.toFixed(2)}` : "Cart"}
        </span>
        {summary.count > 0 ? <span className="badge bg-danger">{summary.count}</span> : null}
      </button>

      {open && (
        <div
          className="dropdown-menu show p-2 shadow"
          style={{ minWidth: 340, right: 0, left: "auto" }}
        >
          {summary.count === 0 ? (
            <div className="px-2 py-2">
              <div className="fw-semibold">Your cart is empty</div>
              <div className="text-muted small">Add a product to get started.</div>
            </div>
          ) : (
            <>
              <div className="px-2 pt-1 pb-2 fw-semibold">Cart</div>

              <div style={{ maxHeight: 360, overflowY: "auto" }}>
                {items.map((item) => {
                  const unit = getUnitPrice(item);
                  const qty = Number(item.qty) || 1;
                  const disabled = updatingId === item.id;

                  return (
                    <div key={item.id} className="border-top px-2 py-2">
                      <div className="d-flex justify-content-between">
                        <div className="me-2">
                          <div className="fw-semibold small">{item.name}</div>

                          {item.options?.length ? (
                            <div className="text-muted small">
                              {item.options.map((o, idx) => (
                                <div key={idx}>
                                  {o.field_name}: {o.label}
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </div>

                        <div className="text-end">
                          <div className="fw-semibold small">${(unit * qty).toFixed(2)}</div>
                          <div className="d-flex align-items-center justify-content-end gap-1 mt-1">
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => changeQty(item, -1)}
                              disabled={disabled || qty <= 1}
                            >
                              −
                            </button>
                            <span style={{ minWidth: 18, textAlign: "center" }}>{qty}</span>
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => changeQty(item, +1)}
                              disabled={disabled}
                            >
                              +
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => removeItem(item.id)}
                              disabled={disabled}
                              title="Remove"
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="border-top mt-2 pt-2 px-2 d-flex justify-content-between">
                <div className="fw-semibold">Total</div>
                <div className="fw-semibold">${summary.total.toFixed(2)}</div>
              </div>

              <div className="px-2 pt-2">
                <Link href="/my-cart" className="btn btn-dark w-100" onClick={() => setOpen(false)}>
                  View full cart
                </Link>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
